import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { formatToPKR, formatDate, downloadPDF } from "@/lib/helper";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { useGetDailyCashReportQuery, useLazyGetDailyCashReportForExportQuery } from "@/services/transactionApi";
import useTableData from "@/components/ui/table/data-table-logic";
import usePagination from "@/hooks/use-pagination";
import Pagination from "@/components/ui/table/pagination-table";
import TableFilters from "@/components/ui/table/data-table-filter";
import { useGetIncomeCategoryQuery } from "@/services/incomeCategoryApi";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import useDocumentTitle from '@/components/ui/common/document-title';

type DailyCashRow = {
  transaction_count: number;
  revenue_type: 'income' | 'donation' | string;
  income_category_id: number;
  income_category_name: string;
  total_amount: number;
  total_tax: number;
  total_sub_total: number;
};

export default function CashReport() {
  useDocumentTitle("Daily Cash Report");
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(new Date());
  const [openFilter, setOpenFilter] = React.useState(false);
  const [filterData, setFilterData] = React.useState<Record<string, any>>({});

  const startDate = selectedDate ? formatDate(selectedDate) : '';
  const endDate = startDate;

  // Fetch income categories for filter
  const { data: incomeCategoryData } = useGetIncomeCategoryQuery({
    org_id: orgId,
    query: "sort_key=id&sort_order=desc&status=active"
  }, {
    skip: !orgId || orgId === 0
  });

  // Process income category options
  const incomeCategoryOptions = React.useMemo(() => {
    return Array.isArray(incomeCategoryData?.data)
      ? incomeCategoryData.data.map((item) => ({
        ...item,
        id: item.id?.toString() || "",
        name: item.name,
      }))
      : [];
  }, [incomeCategoryData]);

  // Filter display configuration
  const filterDisplay = React.useMemo(() => [
    {
      type: "multiselect",
      name: "income_category_ids",
      label: "Income Category",
      options: incomeCategoryOptions?.map((item) => ({
        value: item.id,
        label: item.name,
      })),
      function: (value: (string | number)[]) => {
        setFilterData((prev) => ({
          ...prev,
          income_category_ids: value,
        }));
      },
    },
  ], [incomeCategoryOptions]);

  // central search criteria + query builder
  const { searchCriteria, setSearchCriteria, query } = useTableData({
    limit: 10,
    offset: 0,
    // sort_order: "desc",
    // sort_key: "id",
    load_summary: "true",
    // group_by_keys: "income_category",
    only_srb: "false",
    start_date: startDate,
    end_date: endDate,
  });

  // pagination controls will be created after fetching totals

  // table data (paginated, grouped) - single API call with summary
  const { data: tableDataResp, isLoading } = useGetDailyCashReportQuery({ query, org_id: orgId }, { skip: !startDate });

  const rows: DailyCashRow[] = React.useMemo(() => {
    const raw = (tableDataResp as any)?.data as DailyCashRow[] | undefined;
    return Array.isArray(raw) ? raw : [];
  }, [tableDataResp]);

  const apiTotal = tableDataResp?.filtered_counts || 0;

  // Extract summary totals from single API response
  const summaryTotals = React.useMemo(() => {
    const revenueSummary = (tableDataResp as any)?.revenue_summary;
    const summary = (tableDataResp as any)?.summary;
    
    return {
      incomeTotal: revenueSummary?.income_sales || 0,
      donationTotal: revenueSummary?.donation_sales || 0,
      totalSales: revenueSummary?.total_sales || 0,
    };
  }, [tableDataResp]);

  // Lazy query for export (only called when exporting)
  const [getDailyCashReportForExport, { isLoading: isExporting }] = useLazyGetDailyCashReportForExportQuery();

  // pagination controls using shared hook (after we know totals)
  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination({
    totalRecords: apiTotal,
    searchCriteria,
    setSearchCriteria,
  });

  React.useEffect(() => {
    // update criteria when date changes (preserve existing filters)
    setSearchCriteria((prev: any) => ({
      ...prev,
      start_date: startDate,
      end_date: endDate,
      offset: 0,
    }));
  }, [startDate, endDate, setSearchCriteria]);

  // Reset filter handler
  const resetFilters = () => {
    setFilterData({});
    // Also reset in search criteria
    setSearchCriteria((prev: any) => {
      const { income_category_ids, ...rest } = prev;
      return {
        ...rest,
        offset: 0,
      };
    });
  };

  return (
    <div className="w-full p-5 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Daily Cash Report</h1>
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={"outline"}
                className={cn(
                  "w-[240px] h-8 justify-start text-left font-normal bg-white",
                  !selectedDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {selectedDate ? format(selectedDate, "PPP") : <span>Pick a date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <Button
            className="ml-2 h-8"
            variant="default"
            disabled={!selectedDate || isExporting}
            onClick={async () => {
              // Build export query to fetch all data (no limit, offset 0, with sorting)
              const exportQueryParams = new URLSearchParams();
              exportQueryParams.append("offset", "0");
              exportQueryParams.append("sort_order", "desc");
              exportQueryParams.append("sort_key", "id");
              exportQueryParams.append("load_summary", "true");
              // exportQueryParams.append("group_by_keys", "income_category");
              exportQueryParams.append("only_srb", "false");
              if (startDate) exportQueryParams.append("start_date", startDate);
              if (endDate) exportQueryParams.append("end_date", endDate);
              
              // Add income category filter if selected
              const incomeCategoryIds = (searchCriteria as Record<string, any>).income_category_ids;
              if (incomeCategoryIds && Array.isArray(incomeCategoryIds)) {
                incomeCategoryIds.forEach((id: string | number) => {
                  if (id) exportQueryParams.append("income_category_ids", String(id));
                });
              }
              
              // Fetch all data for export using lazy query (no limit = all data)
              const exportQuery = exportQueryParams.toString();
              const exportResult = await getDailyCashReportForExport({ query: exportQuery });
              
              const exportData = exportResult.data;
              const exportRows = Array.isArray(exportData?.data) ? exportData.data as unknown as DailyCashRow[] : [];
              const title = selectedDate ? `Daily Cash Report - ${format(selectedDate, 'PPP')}` : 'Daily Cash Report';
              
              await downloadPDF(
                {
                  data: exportRows as any,
                  total_counts: exportData?.total_counts || exportRows.length,
                  filtered_counts: exportData?.filtered_counts || exportRows.length,
                  summary: {
                    total: exportData?.revenue_summary?.total_sales || exportData?.summary?.total || 0,
                    sub_total: exportData?.summary?.sub_total || 0,
                    tax: exportData?.summary?.tax || 0,
                    refund_total: exportData?.summary?.refund_total || 0,
                  },
                  revenue_summary: exportData?.revenue_summary
                } as any,
                'daily-cash-report.pdf',
                title,
                { start_date: startDate || undefined, end_date: endDate || undefined },
                'portrait',
                'daily-cash-report'
              );
            }}
          >
            <i className="fa-solid fa-file-pdf mr-2"></i>
            Export PDF
          </Button>
          <button
            className={`border bg-white rounded-full size-3 text-gray-400 p-4 flex items-center justify-center ${Object.keys(filterData).length ? "border-primary" : ""}`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i
                    className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}
                  ></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      </div>

      {/* TableFilters Modal */}
      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={{
          limit: 10,
          offset: 0,
        }}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={(value: any) => {
          // When filters are applied from TableFilters, update both filterData and searchCriteria
          const updatedFilterData = typeof value === 'function' ? value(filterData) : value;
          setFilterData(updatedFilterData);

          // Update search criteria with filter data, preserving start_date and end_date
          setSearchCriteria((prev: any) => {
            const next = { ...prev };
            // Always preserve start_date and end_date from selectedDate
            next.start_date = startDate;
            next.end_date = endDate;
            // Add income_category_ids if present
            if (updatedFilterData.income_category_ids && Array.isArray(updatedFilterData.income_category_ids) && updatedFilterData.income_category_ids.length > 0) {
              next.income_category_ids = updatedFilterData.income_category_ids;
            } else {
              // Remove income_category_ids if not present
              delete next.income_category_ids;
            }
            next.offset = 0;
            return next;
          });
        }}
        filterDisplay={filterDisplay}
        setInputValue={() => { }}
        onReset={resetFilters}
      />

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">Total Sales</p>
              <p className="text-2xl font-bold text-green-800">{formatToPKR(summaryTotals.totalSales)}</p>
            </div>
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <i className="fa-solid fa-chart-line text-green-600"></i>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">Income Sales</p>
              <p className="text-2xl font-bold text-blue-800">{formatToPKR(summaryTotals.incomeTotal)}</p>
            </div>
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 font-semibold text-sm">Rs</span>
            </div>
          </div>
        </div>

        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-indigo-600">Donation Sales</p>
              <p className="text-2xl font-bold text-indigo-800">{formatToPKR(summaryTotals.donationTotal)}</p>
            </div>
            <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
              <i className="fa-solid fa-hand-holding-heart text-indigo-600"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">Report for {selectedDate ? format(selectedDate, 'PPP') : '—'}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <ScrollArea className="w-full">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Income Category</TableHead>
                    <TableHead>Revenue Type</TableHead>
                    <TableHead className="text-right">Total Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={3} className="h-24 text-center">Loading…</TableCell>
                    </TableRow>
                  ) : rows.length ? (
                    rows.map((r, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="capitalize">{r.income_category_name}</TableCell>
                        <TableCell className="capitalize">{r.revenue_type}</TableCell>
                        <TableCell className="text-right">{formatToPKR(r.total_amount || 0)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} className="h-24 text-center">No data</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </div>

          {rows.length > 0 && (
            <Pagination
              limit={Number(searchCriteria.limit)}
              offset={Number(searchCriteria.offset)}
              totalItems={apiTotal}
              onLimitChange={handleLimitChange}
              onNextPage={handleNextPage}
              onPrevPage={handlePrevPage}
              onFirstPage={handleFirstPage}
              onLastPage={handleLastPage}
              isLastPage={isLastPage}
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}
